import { createApi } from "@reduxjs/toolkit/query/react";

import type { IApiResponse } from "@/shared/interfaces/api-response.interface";

import { ApiTags } from "../api-tags.enum";
import customFetchBase from "../custom-fetch-base";

import type { IChatAlertWithContext } from "./chat-alert-details.interface";
import type { ChatAlertPriority, IChatAlertStats, IChatAlertsListResponse } from "./chat-alerts.interface";

interface GetChatAlertsParams {
  districtId?: string;
  limit?: number;
  offset?: number;
  type?: string;
  initiator?: string;
  priority?: string;
  userId?: string;
  chatRoomId?: string;
  createdFrom?: string;
  createdTo?: string;
  includeResolved?: boolean;
  resolvedOnly?: boolean;
}

export const chatAlertsApi = createApi({
  reducerPath: "chatAlertsApi",
  baseQuery: customFetchBase,
  tagTypes: [ApiTags.ChatAlerts],
  endpoints: (builder) => ({
    getChatAlerts: builder.query<IChatAlertsListResponse, GetChatAlertsParams>({
      query: (params) => ({
        url: "/admin/chat-alerts",
        method: "GET",
        params,
      }),
      providesTags: [ApiTags.ChatAlerts],
      transformResponse: (result: IApiResponse<IChatAlertsListResponse>) => result.data,
    }),
    getChatAlertDetails: builder.query<IChatAlertWithContext, string>({
      query: (alertId) => ({
        url: `/admin/chat-alerts/${alertId}`,
        method: "GET",
      }),
      providesTags: [ApiTags.ChatAlerts],
      transformResponse: (result: IApiResponse<IChatAlertWithContext>) => result.data,
    }),
    resolveChatAlert: builder.mutation<{ id: string; resolved: boolean }, string>({
      query: (alertId) => ({
        url: `/admin/chat-alerts/${alertId}/resolve`,
        method: "PATCH",
      }),
      invalidatesTags: [ApiTags.ChatAlerts],
      transformResponse: (result: IApiResponse<{ id: string; resolved: boolean }>) => result.data,
    }),
    updateChatAlertPriority: builder.mutation<{ id: string; priority: ChatAlertPriority }, { alertId: string; priority: ChatAlertPriority }>({
      query: ({ alertId, priority }) => ({
        url: `/admin/chat-alerts/${alertId}/priority`,
        method: "PATCH",
        body: { priority },
      }),
      invalidatesTags: [ApiTags.ChatAlerts],
      transformResponse: (result: IApiResponse<{ id: string; priority: ChatAlertPriority }>) => result.data,
    }),
    reopenChatAlert: builder.mutation<{ id: string; resolved: boolean }, string>({
      query: (alertId) => ({
        url: `/admin/chat-alerts/${alertId}/reopen`,
        method: "PATCH",
      }),
      invalidatesTags: [ApiTags.ChatAlerts],
      transformResponse: (result: IApiResponse<{ id: string; resolved: boolean }>) => result.data,
    }),
    bulkResolveChatAlerts: builder.mutation<{ updated: number }, string[]>({
      query: (ids) => ({
        url: "/admin/chat-alerts/bulk/resolve",
        method: "POST",
        body: { ids },
      }),
      invalidatesTags: [ApiTags.ChatAlerts],
      transformResponse: (result: IApiResponse<{ updated: number }>) => result.data,
    }),
    bulkUpdateChatAlertsPriority: builder.mutation<{ updated: number }, { ids: string[]; priority: ChatAlertPriority }>({
      query: ({ ids, priority }) => ({
        url: "/admin/chat-alerts/bulk/priority",
        method: "POST",
        body: { ids, priority },
      }),
      invalidatesTags: [ApiTags.ChatAlerts],
      transformResponse: (result: IApiResponse<{ updated: number }>) => result.data,
    }),
    getChatAlertStats: builder.query<IChatAlertStats, { districtId?: string }>({
      query: ({ districtId }) => ({
        url: "/admin/chat-alerts/stats",
        method: "GET",
        params: { districtId: districtId || undefined },
      }),
      providesTags: [ApiTags.ChatAlerts],
      transformResponse: (result: IApiResponse<IChatAlertStats>) => result.data,
    }),
  }),
});

export default chatAlertsApi;

export const {
  useGetChatAlertsQuery,
  useGetChatAlertDetailsQuery,
  useResolveChatAlertMutation,
  useUpdateChatAlertPriorityMutation,
  useReopenChatAlertMutation,
  useBulkResolveChatAlertsMutation,
  useBulkUpdateChatAlertsPriorityMutation,
  useGetChatAlertStatsQuery,
} = chatAlertsApi;
